import type { ReactNode } from 'react';
import { ScrollEdge } from '../../../ui';
import { PanePinned } from '../../shell/PaneHost';

/**
 * ONE COLUMN OF THE DASHBOARD ON THE DESKTOP: A BOUNDED SCROLL REGION (round 2, F5).
 *
 * The owner's finding: "Clients to focus on" with 29 rows pushed the whole page down and the
 * right column ended early. At the desktop widths each column is now its own vertical
 * scroller, as tall as the space the rows above it leave, and the page itself stands still.
 *
 * THE HEAD IS THE HOST'S PINNED LINE. The column's title and its controls stay at the top
 * while the rows pass under them, built from the pane host's own parts rather than a second
 * construction: `PanePinned` is the sticky line (`app/shell/PaneHost.tsx`) and it mounts its
 * own `ScrollEdge`, which is what the host's rule for a pinned line asks. The HARD master,
 * as the object board's pinned Group by line uses it, because the line carries controls
 * without a background of their own ("Hard ... ideal for interactive text, controls without
 * backgrounds, or pinned table headers", wwdc: https://developer.apple.com/videos/play/wwdc2025/356/
 * at 11:18). No blur is drawn here: the effect is the kit's master in `ui/ScrollEdge`.
 *
 * The title keeps the existing column head construction (semibold, 15px), because the
 * mockup's 20px section heading is a size the register's type line (L7) does not list. No
 * group box any more (L4: headings above, spacing between); the rows sit on the page. Phone
 * widths never mount this: there the document scrolls and each part carries its own title.
 */
export function DashboardColumn({
  name,
  title,
  adornment,
  trailing,
  children,
}: {
  /** Names the column for the review and its scroll edge (`dashboard-<name>`). */
  readonly name: string;
  readonly title: string;
  /** Beside the title: the chosen tile's symbol and word (brief section 5). */
  readonly adornment?: ReactNode | undefined;
  /** At the trailing edge of the head: the data age (D9). */
  readonly trailing?: ReactNode | undefined;
  readonly children: ReactNode;
}) {
  return (
    <section
      aria-label={title}
      data-dashboard-column={name}
      className="flex min-h-0 min-w-0 flex-col overflow-y-auto"
    >
      <PanePinned>
        <ScrollEdge host={`dashboard-${name}`} style="hard" />
        <header className="flex flex-wrap items-center gap-x-3 gap-y-2 pb-2">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {adornment}
          {trailing !== undefined && <span className="ms-auto">{trailing}</span>}
        </header>
      </PanePinned>
      <div>{children}</div>
    </section>
  );
}
